import { useState, useMemo, useEffect } from 'react';
import { useThrottleEffect } from 'ahooks';
import { useDispatch, useStore, EcoState } from '../../context';
import { useEcoDispatch, useEcoStore } from 'lowcode/utils/context/ecocredits';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import { filterByPoints } from '../utils';
import { DEFAULT_ACTIVE_TAB, POINTS_RANGES } from '../constants';
import { ModuleTab, CouponRule, TranslationFn } from '../types';

interface UseRewardsDataProps {
    locale: string;
    pathUrl?: string;
    isEditStatus?: boolean;
    defauleActiveTab?: string;
    t: TranslationFn;
}

interface UseRewardsDataReturn {
    // State from context
    state: EcoState;
    ecoState: any;
    // Filtered data
    discountList: CouponRule[];
    productList: CouponRule[];
    giftCardList: CouponRule[];
    // All unfiltered data (for tab visibility check)
    allData: CouponRule[];
    // Tabs
    filterTabs: ModuleTab[];
    activeKey: string;
    setActiveKey: (key: string) => void;
    // Points filter
    activeRange: string;
    setActiveRange: (range: string) => void;
    // Actions
    refreshAllRules: () => void;
    dispatchCheckBlackList: () => void;
}

/**
 * Hook for managing rewards data fetching and filtering
 */
export const useRewardsData = ({
    locale,
    pathUrl,
    isEditStatus = false,
    defauleActiveTab = DEFAULT_ACTIVE_TAB,
    t,
}: UseRewardsDataProps): UseRewardsDataReturn => {
    const dispatch = useDispatch();
    const dispatchEco = useEcoDispatch();
    const state = useStore();
    const ecoState = useEcoStore();
    const { timezoneList } = useMemberTimeStore();

    const [activeKey, setActiveKey] = useState(defauleActiveTab);
    const [activeRange, setActiveRange] = useState<string>('');
    const [hasInitializedRange, setHasInitializedRange] = useState(false);

    // Build tabs based on pathUrl
    const allTabs: ModuleTab[] = useMemo(() => {
        return pathUrl
            ? [
                  { key: 'COUPONS_RULES_URL', label: t('discount') },
                  { key: 'FREE_PRODUCTS_RULES_URL', label: t('product_text') },
                  { key: 'THIRD_COUPONS_RULES_URL', label: t('gift_card') },
              ]
            : [
                  { key: 'COUPONS_RULES', label: t('discount') },
                  { key: 'FREE_PRODUCTS_RULES', label: t('product_text') },
                  { key: 'THIRD_COUPONS_RULES', label: t('gift_card') },
              ];
    }, [pathUrl, t]);

    // Filter tabs that have data
    const filterTabs = useMemo(() => {
        return allTabs?.filter((tab) => {
            return state[tab.key]?.length;
        });
    }, [state, allTabs]);

    // Grouped and filtered data
    const { discountList, productList, giftCardList, allData } = useMemo(() => {
        const keyPrefix = pathUrl ? '_URL' : '';
        const coupons = (state[`COUPONS_RULES${keyPrefix}` as keyof EcoState] || []) as CouponRule[];
        const thirdCoupons = (state[`THIRD_COUPONS_RULES${keyPrefix}` as keyof EcoState] || []) as CouponRule[];
        const products = (state[`FREE_PRODUCTS_RULES${keyPrefix}` as keyof EcoState] || []) as CouponRule[];

        // Combine all data for tab visibility check
        const allData = [...coupons, ...thirdCoupons, ...products];

        return {
            discountList: filterByPoints(coupons, activeRange),
            productList: filterByPoints(products, activeRange),
            giftCardList: filterByPoints(thirdCoupons, activeRange),
            allData,
        };
    }, [state, pathUrl, activeRange]);

    // Initialize activeRange to first available range when data loads
    useEffect(() => {
        if (hasInitializedRange || !allData?.length) return;

        const firstAvailableRange = POINTS_RANGES.find((range) => allData.some((item) => range.filter(item.points)));
        if (firstAvailableRange) {
            setActiveRange(firstAvailableRange.key);
            setHasInitializedRange(true);
        }
    }, [allData, hasInitializedRange]);

    /**
     * Refresh all rules data
     */
    const refreshAllRules = () => {
        dispatch({
            type: 'getCouponsRules',
            payload: { t, locale, pathUrl, timezoneList },
        });
        dispatch({
            type: 'getProductRules',
            payload: { locale, pathUrl },
        });
        dispatch({
            type: 'getThiredCouponRules',
            payload: { locale, pathUrl },
        });
    };

    /**
     * Check blacklist
     */
    const dispatchCheckBlackList = () => {
        if (!isEditStatus) {
            dispatch({
                type: 'checkBlackList',
                payload: { locale },
            });
        }
    };

    // Initial data fetch and login check
    useThrottleEffect(() => {
        dispatchCheckBlackList();

        if (!pathUrl) {
            dispatch({
                type: 'getCouponsRules',
                payload: { t, locale, pathUrl: '', timezoneList },
            });
            dispatch({
                type: 'getProductRules',
                payload: { locale, pathUrl: '' },
            });
            dispatch({
                type: 'getThiredCouponRules',
                payload: { locale, pathUrl: '' },
            });
        }
    }, [timezoneList]);

    // Fetch data when pathUrl changes
    useEffect(() => {
        dispatch({
            type: 'getCouponsRules',
            payload: { t, locale, pathUrl, timezoneList },
        });
        dispatch({
            type: 'getProductRules',
            payload: { locale, pathUrl },
        });
        dispatch({
            type: 'getThiredCouponRules',
            payload: { locale, pathUrl },
        });
    }, [pathUrl, timezoneList]);

    // Auto-select first tab when tabs change
    useThrottleEffect(() => {
        if (!filterTabs?.length) return;

        // Check if current active key has data
        const activeList = state[activeKey as keyof EcoState];
        if (!isEditStatus && activeList?.length) return;

        setActiveKey(filterTabs[0].key);
    }, [filterTabs?.length, pathUrl]);

    // Fetch user reward info
    useEffect(() => {
        const queryRewardUserInfo = async () => {
            dispatchEco({
                type: 'changeRewardUserInfo',
                payload: { locale },
            });
        };

        if (!ecoState.rewardUserInfo && !isEditStatus) {
            queryRewardUserInfo();
        }
    }, [ecoState]);

    return {
        state,
        ecoState,
        discountList,
        productList,
        giftCardList,
        allData,
        filterTabs,
        activeKey,
        setActiveKey,
        activeRange,
        setActiveRange,
        refreshAllRules,
        dispatchCheckBlackList,
    };
};
